import { useState, useRef } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u3qg53pcjqs2fcslg0', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverCode = (parsed?.code as string) || '';
      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string || (parsed?.message as string) || responseText;

      if (
        response.ok &&
        serverCode === 'OK' &&
        !serverMsg.toLowerCase().includes('spam') &&
        !serverMsg.toLowerCase().includes('form data is spam')
      ) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - jm天堂官方指南"
        description="了解jm天堂品牌故事、平台信誉与联系方式。jm天堂是深受用户喜爱的内容平台，致力于提供优质的内容资源和服务。"
        keywords="jm天堂,关于jm天堂,jm天堂联系方式,jm天堂品牌,jm天堂信誉"
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: 'jm天堂',
          url: 'https://goldroute.github.io',
          description: 'jm天堂是深受用户喜爱的内容平台，致力于提供优质的内容资源和服务。',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'support@jmtiantang-official.com',
            availableLanguage: ['Chinese'],
          },
        }}
      />

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="mb-16">
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">
            关于jm天堂
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                品牌故事
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                jm天堂诞生于对优质内容资源的追求与热爱。作为一个深受用户喜爱的内容平台，我们始终坚持以用户体验为核心，致力于为每一位用户提供丰富、多样、高品质的内容资源。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                自成立以来，jm天堂已服务了数百万用户，累计提供超过数十万条内容资源。平台涵盖多个内容分类，满足不同用户的多样化需求。无论是初次接触的新用户，还是长期使用的老用户，都能在jm天堂找到属于自己的内容天地。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                我们深知稳定、安全的访问对用户的重要性。因此，我们建立了本官方网站，持续为用户提供最新、最准确的官方入口地址和使用指导，确保每一位用户都能顺利访问jm天堂平台。
              </p>
            </div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20modern%20office%20interior%20with%20soft%20amber%20lighting%2C%20clean%20minimalist%20design%2C%20team%20collaboration%20atmosphere%2C%20cream%20walls%2C%20wooden%20furniture%2C%20professional%20editorial%20photography%2C%20warm%20and%20inviting%20corporate%20style&width=800&height=600&seq=jm-about-brand-01&orientation=landscape"
                alt="jm天堂品牌形象"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
            平台信誉与优势
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-shield-star-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">安全可靠</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                平台采用多重安全防护措施，保障用户账号安全和数据隐私，让用户安心使用。
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-database-2-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">资源丰富</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                海量内容资源库，涵盖多个分类领域，每日更新，满足用户多样化的内容需求。
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-customer-service-2-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">优质服务</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                专业客服团队提供及时的技术支持和问题解答，确保用户获得最佳使用体验。
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-thumb-up-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">口碑卓越</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                数百万用户的信任之选，平台在用户中享有极高的满意度和口碑评价。
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-loop-left-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">持续更新</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                平台内容和技术持续迭代更新，不断优化功能体验，与时俱进满足用户需求。
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-group-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">活跃社区</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                拥有活跃的用户社区，用户可以分享资源、交流心得，共同维护良好的平台生态。
              </p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                联系我们
              </h2>
              <p className="text-foreground-700 leading-relaxed mb-8">
                如果您在使用jm天堂过程中遇到任何问题，或有任何建议和反馈，欢迎通过以下方式与我们取得联系。我们的客服团队将在工作时间内尽快回复您的咨询。
              </p>

              <div className="space-y-5">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">support@jmtiantang-official.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">服务时间</h4>
                    <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-600 flex-shrink-0">
                    <i className="ri-question-answer-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">常见问题</h4>
                    <p className="text-sm text-foreground-600">
                      建议先查看我们的
                      <a href="/category/faq" className="text-primary-600 hover:text-primary-700 mx-1">
                        常见问题
                      </a>
                      页面，可能已经解答了您的疑问。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                  发送消息
                </h3>

                {formStatus === 'success' ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">消息已发送！</h4>
                    <p className="text-sm text-foreground-600 mb-6">感谢您的留言，我们将在工作时间内尽快回复您。</p>
                    <button
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      onClick={() => setFormStatus('idle')}
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                    noValidate
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请简要描述您的问题"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                        placeholder="请详细描述您的问题或建议（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      className="form-hp-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {formStatus === 'error' && formError && (
                      <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          发送中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          发送消息
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}